/**
 * How much a customer may stake on THIS selection right now — derived on the
 * client from `/me` so the slip can say the number BEFORE the server refuses.
 *
 * The server is the authority (0037 delayed-price caps, 0038 aggregate
 * exposure, and the balance-derived cap Boss ruled on 2026-09-22 — migration
 * 0048, briefed to Codex). This is a preview of the same arithmetic, so a
 * customer composing a stake sees "up to $X" instead of discovering the limit
 * from a 422. Every number comes from params; nothing here is hard-coded.
 *
 * The 0048 fields (`maxStakeBalanceBps`, `maxStakeCeilingCents`) are read
 * optionally: until the contract lands they are absent and that rule simply
 * does not apply, so this ships green ahead of the backend.
 */

export interface StakeCapInput {
  playBalanceCents: number;
  feeBps: number;
  decimalOdds: number;
  dataDelayed: boolean;
  params: {
    delayedPriceMaxStakeCents?: number;
    delayedPriceMaxPayoutCents?: number;
    maxStakeBalanceBps?: number;
    maxStakeCeilingCents?: number;
  };
}

export type CapReason = 'balance' | 'delayed_stake' | 'delayed_payout' | 'balance_share' | 'ceiling';

export interface StakeCap {
  /** The largest stake the slip should accept, in cents. 0 means refuse all. */
  maxStakeCents: number;
  /** Which rule produced the number — the tightest one wins. */
  reason: CapReason;
}

/** Largest stake whose stake+fee fits inside the balance: s + floor(s·bps/1e4) ≤ b. */
function balanceAffordable(balanceCents: number, feeBps: number): number {
  if (balanceCents <= 0) return 0;
  // Start from the no-fee bound and walk down — fee flooring makes the closed
  // form off by one in rare cases, and the walk is at most a couple of steps.
  const fits = (s: number) => s + Math.floor((s * feeBps) / 10_000) <= balanceCents;
  let s = Math.floor((balanceCents * 10_000) / (10_000 + feeBps));
  while (s > 0 && !fits(s)) s -= 1;
  while (fits(s + 1)) s += 1;
  return Math.max(0, s);
}

export function computeStakeCap(input: StakeCapInput): StakeCap {
  const first: { cents: number; reason: CapReason } = {
    cents: balanceAffordable(input.playBalanceCents, input.feeBps),
    reason: 'balance',
  };
  const candidates: Array<{ cents: number; reason: CapReason }> = [];

  const p = input.params;

  if (input.dataDelayed) {
    if (p.delayedPriceMaxStakeCents !== undefined) {
      candidates.push({ cents: p.delayedPriceMaxStakeCents, reason: 'delayed_stake' });
    }
    if (p.delayedPriceMaxPayoutCents !== undefined && input.decimalOdds > 0) {
      // payout = floor(stake × odds) ≤ cap  ⇒  stake ≤ floor(cap / odds)
      candidates.push({
        cents: Math.floor(p.delayedPriceMaxPayoutCents / input.decimalOdds),
        reason: 'delayed_payout',
      });
    }
  }

  if (p.maxStakeBalanceBps !== undefined) {
    candidates.push({
      cents: Math.floor((input.playBalanceCents * p.maxStakeBalanceBps) / 10_000),
      reason: 'balance_share',
    });
  }
  if (p.maxStakeCeilingCents !== undefined) {
    candidates.push({ cents: p.maxStakeCeilingCents, reason: 'ceiling' });
  }

  // Tightest wins; on a tie the earlier (more fundamental) reason is kept so
  // "insufficient balance" is never mislabelled as a policy cap.
  let best = first;
  for (const c of candidates) if (c.cents < best.cents) best = c;
  return { maxStakeCents: Math.max(0, best.cents), reason: best.reason };
}

export function describeCapReason(reason: CapReason): string {
  switch (reason) {
    case 'balance':
      return 'limited by your Play Balance';
    case 'delayed_stake':
      return 'the maximum stake on a delayed price';
    case 'delayed_payout':
      return 'the maximum payout on a delayed price';
    case 'balance_share':
      return 'the maximum share of your Play Balance per bet';
    case 'ceiling':
      return 'the maximum stake per bet';
  }
}
